import {
  Checkbox,
  DialogStep,
  HTMLSelect,
  Label,
  MultistepDialog,
} from '@blueprintjs/core';
import { useState } from 'react';
import { insurance } from '@axo/shared/data-access/types';
import DateTimePicker from '../DatePicker/DateTimePicker';
import { formatDatetime } from '../../utils/formatDate';
import { useTerminateInsurance } from '@axo/shared/data-access/hooks';

export interface TerminationWizardProps {
  isOpen: boolean;

  onClose(): void;

  id: string;
}

export function TerminationWizard({
  id,
  isOpen,
  onClose,
}: TerminationWizardProps) {
  const [reason, setReason] = useState<
    insurance.InsuranceTerminateReason | 'none'
  >('none');
  const [terminatedAt, setTerminatedAt] = useState<Date | null>(
    () => new Date(),
  );
  const terminate = useTerminateInsurance();

  return (
    <MultistepDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Terminate insurance"
      icon="trash"
      finalButtonProps={{
        intent: 'danger',
        text: 'Terminate',
        icon: 'delete',
        loading: terminate.isLoading,
        onClick: async () => {
          if (reason === 'none') return;
          await terminate.mutateAsync({ id, reason, at: terminatedAt });
          onClose();
        },
      }}
    >
      <DialogStep
        id="reason"
        title="Reason"
        nextButtonProps={{ disabled: reason === 'none' }}
        panel={
          <div style={{ padding: '2rem', display: 'inline-block' }}>
            <Label>
              Termination reason
              <HTMLSelect
                value={reason}
                onChange={(e) =>
                  setReason(
                    e.target.value as insurance.InsuranceTerminateReason,
                  )
                }
              >
                <option disabled={reason !== 'none'} value="none">
                  Select one
                </option>
                {Object.keys(insurance.InsuranceTerminateReason).map((key) => (
                  <option value={key} key={key}>
                    {key}
                  </option>
                ))}
              </HTMLSelect>
            </Label>
          </div>
        }
      />
      <DialogStep
        id="date"
        title="Date"
        panel={
          <div style={{ padding: '2rem', display: 'inline-block' }}>
            <Checkbox
              label="Specify date"
              checked={terminatedAt !== null}
              onChange={() => setTerminatedAt((x) => (x ? null : new Date()))}
            />
            <DateTimePicker
              disabled={!terminatedAt}
              onChange={(date) => {
                setTerminatedAt(new Date(date));
              }}
              value={terminatedAt?.toISOString()}
            />
          </div>
        }
      />
      <DialogStep
        id="finalize"
        title="Finalize"
        panel={
          <div style={{ padding: '2rem', display: 'inline-block' }}>
            Will terminate insurance with reason: <strong>{reason}</strong>,{' '}
            {terminatedAt ? (
              <>
                and date <strong>{formatDatetime(terminatedAt)}</strong>
              </>
            ) : (
              <>without a specified termination date</>
            )}
          </div>
        }
      />
    </MultistepDialog>
  );
}
